import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {DVIREntity, InvoiceGroupingDefaultEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency";
import {ActionIcon, Group} from "@mantine/core";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useCallback} from "react";
import {usePagination} from "../DataTable/usePagination";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {sortBy} from "lodash";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: InvoiceGroupingDefaultEntity[] = []

export type InvoiceGroupingDefaultDataTableProps = EntityDataTableProps<InvoiceGroupingDefaultEntity> & {
  onEdit?: (record: InvoiceGroupingDefaultEntity) => void
  onDelete?: (record: InvoiceGroupingDefaultEntity) => void
}

export const InvoiceGroupingDefaultDataTable = (props: InvoiceGroupingDefaultDataTableProps) => {
  const {
    records: groupings = EMPTY_ARRAY,
    recordsPerPageOptions,
    onEdit,
    onDelete,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: groupings,
    sortStatus: {columnAccessor: 'rateModelName', direction: 'asc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const onEditClick = useCallback((record: InvoiceGroupingDefaultEntity) => {
    if (onEdit) {
      onEdit(record)
    }
  }, [onEdit])

  const onDeleteClick = useCallback((record: InvoiceGroupingDefaultEntity) => {
    if (onDelete) {
      onDelete(record)
    }
  }, [onDelete])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          title: t("table.entity.invoicegroupingdefault.column.ratemodel", "Rate Model"),
          accessor: "rateModelName",
          width: 100,
          render: ({rateModelName}) => (
            <DefaultText value={rateModelName}/>
          ),
        },
        {
          title: t("table.entity.invoicegroupingdefault.column.object", "Object"),
          accessor: "objectId",
          width: 100,
          render: ({objectId, objectName}) => {
            return objectId ? <DefaultText value={objectName}/> : undefined
          }
        },
        {
          title: t("table.entity.invoicegroupingdefault.column.type", "Type"),
          accessor: "type",
          width: 100,
        },
        {
          title: t("table.entity.invoicegroupingdefault.column.invoicegroupby", "Group By"),
          accessor: "invoiceGroupBy",
          width: 100,
        },
        {
          title: t("table.entity.invoicegroupingdefault.column.invoicemaxcost", "Max Cost"),
          accessor: "invoiceMaxCost",
          width: 100,
          render: ({invoiceMaxCost}) => {
            return invoiceMaxCost ? <FormattedCurrency value={invoiceMaxCost}/> : undefined
          }
        },
        {
          title: t("table.entity.invoicegroupingdefault.column.invoicemaxlineitem", "Max Line Items"),
          accessor: "invoiceMaxLineItem",
          width: 100,
        },
        {
          title: '',
          accessor: "actions",
          width: 100,
          textAlignment: 'right',
          render: (record) => (
            <Group spacing={4} position="right" noWrap>
              {onEdit &&
                <ActionIcon onClick={() => onEditClick(record)}>
                  <IconEdit size={16}/>
                </ActionIcon>
              }
              {onDelete &&
                <ActionIcon color="red" onClick={() => onDeleteClick(record)}>
                  <IconTrash size={16}/>
                </ActionIcon>
              }
            </Group>
          )
        }
      ]}
    />
  )
}
